import { useRef, useState } from 'react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { CharacterCount } from '@/components/CharacterCount';
import { InfoHint } from '@/components/InfoHint';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { EudamedStatus, EuIdentifiers, UdiDiRow, UdiIssuingEntity } from '../api';
import {
  BLANK_UDI_ROW,
  UDI_ISSUING_ENTITIES,
  canAddBasicUdiDi,
  canAddUdiDi,
  euIdentifiersSummary,
  hasEuIdentifiers,
  keptBasicUdiDis,
  linkedRows,
  withAnotherBasicUdiDi,
  withBasicUdiDi,
  withoutBasicUdiDi,
} from '../eu-identifiers';
import {
  BASIC_UDI_DI_MAX,
  BASIC_UDI_DIS_MAX,
  UDI_CONFIGURATION_MAX,
  UDI_DI_MAX,
  UDI_DI_ROWS_MAX,
  UDI_PACKAGING_LEVEL_MAX,
} from '../field-limits';

/** The device's EU identifiers, under its Countries & Regulatory Classification (RA acceptance
 *  checklist B.1; MDR / IVDR Art. 27, Annex VI Part C): a Basic UDI-DI per device model (most
 *  products have one), and one row per UDI-DI (a UDI-DI per configuration and per packaging level),
 *  added and removed like the company account's sites. With several Basic UDI-DIs each UDI-DI says
 *  which it belongs to. All optional: left blank, the EU dossier asks for the UDI assignment plan.
 *  ``onCommit`` is the save (edit page): on leaving a field, and at once when a row is removed or a
 *  pick is made. Wording pending RA review.
 *
 *  Layout (Marc, 2026-10-21: "very crowded and not intuitive"): the UDI-DIs are one compact table
 *  (column names once, one line each, a trash icon), counters show only while typing or near the
 *  limit, empty boxes show an example. */
const NOT_SET = 'not-set';

/** The help behind each "i" — kept out of the form so it isn't crowded (Marc, 2026-10-21: "the same
 *  i like in the company account"). Wording pending RA review. */
const HINTS = {
  section:
    'Assigned through your UDI issuing entity (GS1, HIBCC, ICCBBA or IFA) and registered in EUDAMED. Stated on ' +
    "the EU dossier's cover, the EU Declaration of Conformity and the UDI part. Leave blank if not yet assigned, " +
    'and the dossier will ask for your UDI assignment plan.',
  entity: 'The entity whose format your UDIs follow. A GS1 UDI-DI is a GTIN (digits only).',
  eudamed:
    "The device's registration in the EUDAMED UDI / Device module (MDR Article 29, IVDR Article 26). Required " +
    "before the device is placed on the EU market. Stated on the EU dossier's cover and UDI part.",
  basic:
    'One per device model. Models with the same intended purpose, risk class and essential design share one. ' +
    'Most products have one.',
  udiDis: 'One for each configuration and each packaging level placed on the market.',
} as const;

/** A counter is noise on a 14-character code: show it while the box is being typed in, or once the
 *  text nears its limit (80%), when the limit matters. */
const NEAR_LIMIT = 0.8;

/** One value that wraps in a box growing with it — a one-line box cut long text off at its edge (Marc,
 *  2026-10-21). Starts one line tall; Enter adds no line break (a code or a label is one line). Codes
 *  have no spaces, so ``code`` breaks them anywhere. Same growth as the company account's long text. */
function WrapField({ code = false, className, ...props }: React.ComponentProps<typeof Textarea> & { code?: boolean }) {
  return (
    <Textarea
      rows={1}
      spellCheck={code ? false : undefined}
      {...props}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
      }}
      className={cn(
        'min-h-9 bg-white resize-none supports-[not(field-sizing:content)]:resize-y',
        code ? 'break-all' : 'break-words',
        className,
      )}
    />
  );
}

const TRASH = 'shrink-0 text-error hover:text-error hover:bg-error/10';

export function EuIdentifiersFields({
  value,
  onChange,
  onCommit,
  defaultOpen,
}: {
  value: EuIdentifiers;
  onChange: (next: EuIdentifiers) => void;
  onCommit?: (next: EuIdentifiers) => void;
  /** Whether the box starts open. Default: open while empty (to be filled), closed once something is
   *  entered, showing a one-line summary (Marc, 2026-10-21: the box was the tallest thing on the page). */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(() => defaultOpen ?? !hasEuIdentifiers(value));
  // The box being typed in — its counter shows (see NEAR_LIMIT).
  const [focused, setFocused] = useState<string | null>(null);
  // The UDI-DI rows of the Basic UDI-DI being edited, noted when its box gains focus: the edit moves
  // exactly these (see linkedRows), never rows of another model whose value it passes through.
  const editingRows = useRef<number[]>([]);
  const setRow = (index: number, patch: Partial<UdiDiRow>) =>
    onChange({ ...value, udi_dis: value.udi_dis.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  const save = (next: EuIdentifiers) => {
    onChange(next);
    onCommit?.(next);
  };
  const removeRow = (index: number) => save({ ...value, udi_dis: value.udi_dis.filter((_, i) => i !== index) });
  // An empty list still shows one field to type the first Basic UDI-DI into.
  const basics = value.basic_udi_dis.length > 0 ? value.basic_udi_dis : [''];
  const several = basics.length > 1;
  const models = keptBasicUdiDis(value);
  const linking = models.length > 1;

  /** Focus + blur wiring and the counter for one box. */
  const field = (key: string, text: string, max: number) => ({
    props: {
      maxLength: max,
      onFocus: () => setFocused(key),
      onBlur: () => {
        setFocused(null);
        onCommit?.(value);
      },
    },
    counter:
      focused === key || text.length >= max * NEAR_LIMIT ? <CharacterCount value={text} max={max} /> : null,
  });

  return (
    <div className="space-y-5 p-4 rounded-lg bg-line/15 border border-line">
      <div>
        {/* The "i" sits right after the title, on its line (Marc's screenshots: after the whole
            header it landed past the summary, and top-aligned it sat high). */}
        <div className="flex items-center gap-x-1.5">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 text-left"
          >
            <ChevronDown
              className={cn('size-4 shrink-0 text-muted transition-transform', !open && '-rotate-90')}
              aria-hidden
            />
            <span className="text-[13px] font-semibold text-navy">EU device identifiers</span>
          </button>
          <InfoHint hint={HINTS.section} />
        </div>
        {!open && hasEuIdentifiers(value) ? (
          <p data-testid="eu-identifiers-summary" className="m-0 mt-1 pl-6 text-[12.5px] text-muted break-words">
            {euIdentifiersSummary(value).map((item, i) => (
              <span key={item.label}>
                {i > 0 ? ' · ' : null}
                <strong className="font-semibold text-ink">{item.label}:</strong> {item.value}
              </span>
            ))}
          </p>
        ) : null}
      </div>

      {open ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 max-w-3xl">
            <div className="space-y-1.5">
              <div className="flex items-center gap-x-1.5">
                <Label htmlFor="eu-udi-issuing-entity" className="text-[13px] text-navy">
                  Issuing entity
                </Label>
                <InfoHint hint={HINTS.entity} />
              </div>
              <Select
                value={value.issuing_entity ?? NOT_SET}
                onValueChange={(v) => save({ ...value, issuing_entity: v === NOT_SET ? null : (v as UdiIssuingEntity) })}
              >
                <SelectTrigger id="eu-udi-issuing-entity" aria-label="Issuing entity" className="w-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOT_SET}>Not set</SelectItem>
                  {UDI_ISSUING_ENTITIES.map((e) => (
                    <SelectItem key={e} value={e}>
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-x-1.5">
                <Label htmlFor="eu-eudamed-status" className="text-[13px] text-navy">
                  EUDAMED registration
                </Label>
                <InfoHint hint={HINTS.eudamed} />
              </div>
              <Select
                value={value.eudamed_status ?? NOT_SET}
                onValueChange={(v) => save({ ...value, eudamed_status: v === NOT_SET ? null : (v as EudamedStatus) })}
              >
                <SelectTrigger id="eu-eudamed-status" aria-label="EUDAMED registration" className="w-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NOT_SET}>Not set</SelectItem>
                  <SelectItem value="registered">Registered in EUDAMED</SelectItem>
                  <SelectItem value="not_registered">Not yet registered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center gap-x-1.5">
                <Label htmlFor="eu-basic-udi-di-0" className="text-[13px] text-navy">
                  {several ? 'Basic UDI-DIs' : 'Basic UDI-DI'}
                </Label>
                <InfoHint hint={HINTS.basic} />
              </div>
              <div className="space-y-2">
                {basics.map((b, i) => {
                  const label = several ? `Basic UDI-DI #${i + 1}` : 'Basic UDI-DI';
                  const f = field(`basic-${i}`, b, BASIC_UDI_DI_MAX);
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-start gap-1 sm:max-w-[calc(50%-0.5rem)]">
                        <WrapField
                          code
                          id={`eu-basic-udi-di-${i}`}
                          aria-label={label}
                          placeholder={i === 0 ? 'e.g. 7350012PGR100X7' : undefined}
                          value={b}
                          onChange={(e) => onChange(withBasicUdiDi(value, i, e.target.value, editingRows.current))}
                          {...f.props}
                          onFocus={() => {
                            f.props.onFocus();
                            editingRows.current = linkedRows(value, i);
                          }}
                        />
                        {several ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${label}`}
                            onClick={() => save(withoutBasicUdiDi(value, i))}
                            className={TRASH}
                          >
                            <Trash2 />
                          </Button>
                        ) : null}
                      </div>
                      <div className="sm:max-w-[calc(50%-0.5rem)]">{f.counter}</div>
                    </div>
                  );
                })}
              </div>
              {!basics[0]?.trim() ? null : canAddBasicUdiDi(value) ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onChange(withAnotherBasicUdiDi(value))}
                  className="mt-1"
                >
                  <Plus />
                  Add another Basic UDI-DI
                </Button>
              ) : (
                <p className="text-[12.5px] text-muted m-0">
                  {BASIC_UDI_DIS_MAX} Basic UDI-DIs is the most a product can list.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-x-1.5">
              <div className="text-[13px] font-semibold text-navy">UDI-DIs</div>
              <InfoHint hint={HINTS.udiDis} />
            </div>
            {value.udi_dis.length > 0 ? (
              <div className="rounded-lg border border-line bg-white">
                <div
                  aria-hidden
                  className={cn(
                    'hidden sm:grid gap-3 px-3 pt-2.5 pb-1 text-[12px] font-semibold text-muted',
                    linking
                      ? 'sm:grid-cols-[1.1fr_1fr_1.3fr_1fr_2.25rem]'
                      : 'sm:grid-cols-[1.1fr_1.3fr_1fr_2.25rem]',
                  )}
                >
                  <span>UDI-DI</span>
                  {linking ? <span>Basic UDI-DI</span> : null}
                  <span>Configuration / model</span>
                  <span>Packaging level</span>
                  <span />
                </div>
                {value.udi_dis.map((r, i) => {
                  const code = field(`udi-${i}`, r.udi_di, UDI_DI_MAX);
                  const config = field(`config-${i}`, r.configuration, UDI_CONFIGURATION_MAX);
                  const pkg = field(`pkg-${i}`, r.packaging_level, UDI_PACKAGING_LEVEL_MAX);
                  const mobileLabel = 'sm:hidden text-[12px] font-semibold text-muted';
                  return (
                    <div
                      key={i}
                      className={cn(
                        'grid grid-cols-1 gap-3 items-start px-3 py-2.5',
                        i > 0 && 'border-t border-line',
                        linking
                          ? 'sm:grid-cols-[1.1fr_1fr_1.3fr_1fr_2.25rem]'
                          : 'sm:grid-cols-[1.1fr_1.3fr_1fr_2.25rem]',
                      )}
                    >
                      <div className="space-y-1">
                        <span className={mobileLabel}>UDI-DI</span>
                        <WrapField
                          code
                          id={`eu-udi-di-${i}`}
                          aria-label="UDI-DI"
                          placeholder="e.g. 07350012345679"
                          value={r.udi_di}
                          onChange={(e) => setRow(i, { udi_di: e.target.value })}
                          {...code.props}
                        />
                        {code.counter}
                      </div>
                      {linking ? (
                        <div className="space-y-1">
                          <span className={mobileLabel}>Basic UDI-DI</span>
                          <Select
                            value={models.includes(r.basic_udi_di.trim()) ? r.basic_udi_di.trim() : NOT_SET}
                            onValueChange={(v) =>
                              save({
                                ...value,
                                udi_dis: value.udi_dis.map((x, j) =>
                                  j === i ? { ...x, basic_udi_di: v === NOT_SET ? '' : v } : x,
                                ),
                              })
                            }
                          >
                            <SelectTrigger
                              id={`eu-udi-basic-${i}`}
                              aria-label="Belongs to Basic UDI-DI"
                              className="w-full bg-white"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NOT_SET}>Not chosen</SelectItem>
                              {models.map((m) => (
                                <SelectItem key={m} value={m}>
                                  {m}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        <span className={mobileLabel}>Configuration / model</span>
                        <WrapField
                          id={`eu-udi-config-${i}`}
                          aria-label="Configuration / model"
                          placeholder="e.g. PGR-100"
                          value={r.configuration}
                          onChange={(e) => setRow(i, { configuration: e.target.value })}
                          {...config.props}
                        />
                        {config.counter}
                      </div>
                      <div className="space-y-1">
                        <span className={mobileLabel}>Packaging level</span>
                        <WrapField
                          id={`eu-udi-pkg-${i}`}
                          aria-label="Packaging level"
                          placeholder="e.g. unit, box of 10"
                          value={r.packaging_level}
                          onChange={(e) => setRow(i, { packaging_level: e.target.value })}
                          {...pkg.props}
                        />
                        {pkg.counter}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove UDI-DI #${i + 1}`}
                        onClick={() => removeRow(i)}
                        className={TRASH}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {canAddUdiDi(value) ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => onChange({ ...value, udi_dis: [...value.udi_dis, { ...BLANK_UDI_ROW }] })}
              >
                <Plus />
                {value.udi_dis.length === 0 ? 'Add UDI-DI' : 'Add another UDI-DI'}
              </Button>
            ) : (
              <p className="text-[12.5px] text-muted m-0">{UDI_DI_ROWS_MAX} UDI-DIs is the most a product can list.</p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
